import React, { useMemo, useEffect, useState } from 'react';
import { LIST_ZOOM } from '../../../constants/ecp-locator';
import { IEcpStore } from '../../../rest/ecp-locator';
import { getLatLng, getMarkerIcon } from '../../../utils/ecp-locator';
import { unwrapLatLng } from '../../../utils/latlng/latlng';
import { IMarkerProps } from './marker-cluster.models';
import { EcpStoreType } from '../../../rest/ecp-locator';
import { useStyles } from './marker-cluster.styles';

interface YMapClustererProps {
  'data-testid'?: string;
  features: EcpStoreType[];
  method: unknown;
  maxZoom: number;
  marker: (feature: unknown) => React.ReactNode;
  cluster: (coordinates: [number, number], features: EcpStoreType[]) => React.ReactNode;
}

type ClustererModule = Record<string, unknown> & {
  YMapClusterer: React.ComponentType<YMapClustererProps>;
  clusterByGrid: (options: { gridSize: number }) => unknown;
};

export const MarkerCluster = ({
  ecpStores,
  markerCode,
  onMarkerClick,
  onClusterClick,
  ymapComponents,
}: IMarkerProps & { ymapComponents: any }): JSX.Element | null => {
  const [clustererModule, setClustererModule] = useState<ClustererModule | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const classes = useStyles();

  useEffect(() => {
    const loadClusterer = async () => {
      if (!window.ymaps3) {
        setIsLoading(false);
        return;
      }

      try {
        const clustererNs = await window.ymaps3.import('@yandex/ymaps3-clusterer@0.0.1');
        setClustererModule(clustererNs as unknown as ClustererModule);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    if (ymapComponents) {
      loadClusterer();
    }
  }, [ymapComponents]);

  const storesById = useMemo(() => {
    const m = new Map<string, IEcpStore>();
    for (const s of ecpStores) {
      const id = String(s.code ?? `${s.latitude}_${s.longitude}`);
      m.set(id, s);
    }
    return m;
  }, [ecpStores]);

  const features: EcpStoreType[] = useMemo(() => {
    return ecpStores
      .map((store) => {
        const { latitude, longitude } = unwrapLatLng(getLatLng(store));
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
        const id = String(store.code ?? `${latitude}_${longitude}`);
        return {
          type: 'Feature',
          id,
          geometry: { type: 'Point', coordinates: [longitude, latitude] as [number, number] },
        };
      })
      .filter(Boolean) as unknown as EcpStoreType[];
  }, [ecpStores]);

  if (isLoading || !clustererModule || !ymapComponents) {
    return null;
  }

  const { reactify, YMapMarker } = ymapComponents;

  if (!reactify) {
    return null;
  }

  const reactifiedModule = reactify.module(clustererModule);
  const YMapClusterer = reactifiedModule.YMapClusterer as unknown as React.ComponentType<YMapClustererProps>;
  const { clusterByGrid } = clustererModule;

  const handleMarkerClick = (event: React.MouseEvent, store: IEcpStore) => {
    onMarkerClick?.(event, store);
  };

  const handleClusterClick = (event: React.MouseEvent, clusterFeatures: unknown[]) => {
    event.stopPropagation();
    onClusterClick?.(clusterFeatures);
  };

  const marker = (feature: any) => {
    const store = storesById.get(feature.id);
    if (!store) return null;
    const { url, width, height } = getMarkerIcon(store.type, store.code, markerCode);
    return (
      <YMapMarker key={feature.id} coordinates={feature.geometry.coordinates}>
        <button
          type="button"
          onClick={(e) => handleMarkerClick(e, store)}
          className={classes.markerButton}
          aria-label={`Показать данные ${store?.name ?? store?.code ?? 'store'}`}
          title={`${store?.name ?? ''} ${store?.street ?? ''}`}
        >
          <img src={url} alt="" width={width} height={height} draggable={false} />
        </button>
      </YMapMarker>
    );
  };

  const cluster = (coordinates: [number, number], clusterFeatures: unknown[]) => {
    const count = clusterFeatures.length;

    let background = '#00A3E0';
    let size = 30;
    let fontSize = 12;

    if (count >= 200) {
      background = '#A02C8D';
      size = 70;
      fontSize = 20;
    } else if (count >= 100) {
      background = '#133F7A';
      size = 60;
      fontSize = 18;
    } else if (count >= 50) {
      background = '#0A7CC1';
      size = 50;
      fontSize = 16;
    } else if (count >= 20) {
      background = '#0A7CC1';
      size = 40;
      fontSize = 14;
    } else if (count >= 10) {
      background = '#0A7CC1';
      size = 35;
      fontSize = 13;
    }

    return (
      <YMapMarker coordinates={coordinates}>
        <div
          onClick={(e) => handleClusterClick(e, clusterFeatures)}
          className={classes.clusterBase}
          style={{
            width: size,
            height: size,
            background,
            fontSize,
          }}
        >
          {count}
        </div>
      </YMapMarker>
    );
  };

  return (
    <YMapClusterer
      data-testid="clusterer"
      features={features}
      method={clusterByGrid({ gridSize: 64 })}
      maxZoom={LIST_ZOOM}
      marker={marker}
      cluster={cluster}
    />
  );
};
